import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
} from '@angular/core'
import { NgClass, NgForOf, NgIf, NgOptimizedImage } from '@angular/common'
import { LogoAssets, logoAssetsToken } from 'shared-components'
import {
  Card,
  trickNull,
  GamePlayer,
  gameNull,
  Game,
  CardArray,
} from 'game-services'
import { CardComponent } from '../card/card.component'
import { playerScoreCardNull } from 'game-play-services'
import { GameInfoSectionComponent } from '../game-info-section/game-info-section.component'
import { MatIcon } from '@angular/material/icon'
import { MatButton, MatIconButton } from '@angular/material/button'

export interface DashboardUserAction {
  readonly buttonLabel: string
  readonly value: string
}

@Component({
  selector: 'sans-dashboard',
  standalone: true,
  imports: [
    CardComponent,
    NgForOf,
    NgIf,
    NgOptimizedImage,
    NgClass,
    GameInfoSectionComponent,
    MatIcon,
    MatIconButton,
    MatButton,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnChanges {
  @Input() public game: Game = gameNull
  @Input() public talonCards: Card[] = []
  @Input() public userCards: Card[] = []
  @Input() public rightOpponentCards: Card[] = []
  @Input() public leftOpponentCards: Card[] = []
  @Input() public isUserCardsSelectable = false
  @Input() public userPlayerScoreCard = playerScoreCardNull
  @Input() public leftOpponentPlayerScoreCard = playerScoreCardNull
  @Input() public rightOpponentPlayerScoreCard = playerScoreCardNull
  @Input() public userActions: DashboardUserAction[] = []

  @Output() userCardSelected = new EventEmitter<Card>()
  @Output() talonCardSelected = new EventEmitter<Card>()
  @Output() userActionSelected = new EventEmitter<DashboardUserAction>()

  public userUsername = ''
  public isUserTurnToPlay = false
  public shouldShowUserTrick = false
  public userTrick = trickNull
  public shouldShowUserTricksWon = false
  public userTricksWon = 0
  public userGamePlayerOrderMessage = ''
  public userPassFailLabel = ''

  public leftOpponentUsername = ''
  public shouldShowLeftOpponentCardsFaceUp = false
  public shouldShowLeftOpponentCards = true
  public isLeftOpponentTurnToPlay = false
  public shouldShowLeftOpponentTrick = false
  public leftOpponentTrick = trickNull
  public shouldShowLeftOpponentTricksWon = false
  public leftOpponentTricksWon = 0
  public leftOpponentGamePlayerOrderMessage = ''
  public leftOpponentPassFailLabel = ''

  public rightOpponentUsername = ''
  public shouldShowRightOpponentCardsFaceUp = false
  public shouldShowRightOpponentCards = false
  public isRightOpponentTurnToPlay = false
  public shouldShowRightOpponentTrick = false
  public rightOpponentTrick = trickNull
  public shouldShowRightOpponentTricksWon = false
  public rightOpponentTricksWon = 0
  public rightOpponentGamePlayerOrderMessage = ''
  public rightOpponentPassFailLabel = ''

  public shouldShowTalonCardsOnTable = false
  public shouldShowTalonCardsOnTableFaceUp = false
  public shouldShowUserActions = false
  public isTalonCardsSelectable = false
  public shouldShowPassFailStatus = false

  public showRightOpponentCardsAriaLabel = $localize`:Dashboard component|Show right opponent cards aria label:Show right opponent cards`
  public showLeftOpponentCardsAriaLabel = $localize`:Dashboard component|Show left opponent cards aria label:Show left opponent cards`

  private forehandMessage = $localize`:Dashboard component|Forehand message:Forehand`
  private middlehandMessage = $localize`:Dashboard component|Middlehand message:Middlehand`
  private rearhandMessage = $localize`:Dashboard component|Rearhand message:Rearhand`

  private passedGameLabel = $localize`:Dashboard component|Player passed game label:Passed`
  private failedGameLabel = $localize`:Dashboard component|Player failed game label:Failed`

  private hasUserPassedGame = false
  private hasLeftOpponentPassedGame = false
  private hasRightOpponentPassedGame = false
  private highlightedCards: Card[] = []

  public constructor(@Inject(logoAssetsToken) public logoAssets: LogoAssets) {}

  public ngOnChanges(): void {
    this.userUsername = this.game.user.username
    this.isUserTurnToPlay = this.game.user.isTurnToPlay
    this.shouldShowUserTrick = this.game.user.shouldShowTrick
    this.userTrick = this.game.user.trick
    this.shouldShowUserTricksWon = this.game.user.shouldShowTricksWon
    this.userTricksWon = this.game.user.tricksWon
    this.hasUserPassedGame = this.game.user.hasPassedGame
    this.userGamePlayerOrderMessage = this.getGamePlayerOrderMessage(
      this.game.user,
    )
    this.userPassFailLabel = this.getUserPassFailLabel()

    this.leftOpponentUsername = this.game.leftOpponent.username
    this.shouldShowLeftOpponentCardsFaceUp =
      this.game.leftOpponent.shouldShowCardsFaceUp
    this.isLeftOpponentTurnToPlay = this.game.leftOpponent.isTurnToPlay
    this.shouldShowLeftOpponentTrick = this.game.leftOpponent.shouldShowTrick
    this.leftOpponentTrick = this.game.leftOpponent.trick
    this.shouldShowLeftOpponentTricksWon =
      this.game.leftOpponent.shouldShowTricksWon
    this.leftOpponentTricksWon = this.game.leftOpponent.tricksWon
    this.hasLeftOpponentPassedGame = this.game.leftOpponent.hasPassedGame
    this.leftOpponentGamePlayerOrderMessage = this.getGamePlayerOrderMessage(
      this.game.leftOpponent,
    )
    this.leftOpponentPassFailLabel = this.getLeftOpponentPassFailLabel()

    this.rightOpponentUsername = this.game.rightOpponent.username
    this.shouldShowRightOpponentCardsFaceUp =
      this.game.rightOpponent.shouldShowCardsFaceUp
    this.isRightOpponentTurnToPlay = this.game.rightOpponent.isTurnToPlay
    this.shouldShowRightOpponentTrick = this.game.rightOpponent.shouldShowTrick
    this.rightOpponentTrick = this.game.rightOpponent.trick
    this.shouldShowRightOpponentTricksWon =
      this.game.rightOpponent.shouldShowTricksWon
    this.rightOpponentTricksWon = this.game.rightOpponent.tricksWon
    this.hasRightOpponentPassedGame = this.game.rightOpponent.hasPassedGame
    this.rightOpponentGamePlayerOrderMessage = this.getGamePlayerOrderMessage(
      this.game.rightOpponent,
    )
    this.rightOpponentPassFailLabel = this.getRightOpponentPassFailLabel()

    this.shouldShowTalonCardsOnTable = this.game.shouldShowTalonCardsOnTable
    this.shouldShowTalonCardsOnTableFaceUp =
      this.game.shouldShowTalonCardsOnTableFaceUp
    this.shouldShowUserActions = this.game.shouldShowUserActions
    this.isTalonCardsSelectable = this.game.isTalonCardsSelectable
    this.highlightedCards = this.game.highlightedCards
    this.shouldShowPassFailStatus = this.game.shouldShowPassFailStatus
  }

  public cardTrackBy(_: number, card: Card): string {
    return `${card.rank}${card.suite}`
  }

  public shouldHighlightCard(card: Card): boolean {
    return CardArray.from(this.highlightedCards).contains(card)
  }

  public onUserCardSelected(card: Card): void {
    if (!this.isUserCardsSelectable) {
      return
    }
    this.userCardSelected.next(card)
  }

  public onTalonCardSelected(card: Card): void {
    if (!this.isTalonCardsSelectable) {
      return
    }
    this.talonCardSelected.next(card)
  }

  public onUserActionSelected(userAction: DashboardUserAction): void {
    this.userActionSelected.next(userAction)
  }

  public onShowRightOpponentCards(): void {
    this.shouldShowLeftOpponentCards = false
    this.shouldShowRightOpponentCards = true
  }

  public onShowLeftOpponentCards(): void {
    this.shouldShowLeftOpponentCards = true
    this.shouldShowRightOpponentCards = false
  }

  private getGamePlayerOrderMessage(player: GamePlayer): string {
    if (player.getIsForehand()) {
      return this.forehandMessage
    }

    if (player.getIsMiddlehand()) {
      return this.middlehandMessage
    }

    return this.rearhandMessage
  }

  private getUserPassFailLabel(): string {
    return this.hasUserPassedGame ? this.passedGameLabel : this.failedGameLabel
  }

  private getRightOpponentPassFailLabel(): string {
    return this.hasRightOpponentPassedGame
      ? this.passedGameLabel
      : this.failedGameLabel
  }

  private getLeftOpponentPassFailLabel(): string {
    return this.hasLeftOpponentPassedGame
      ? this.passedGameLabel
      : this.failedGameLabel
  }
}
